import {
	type Manufacturer,
	createManufacturer,
	getManufacturers,
} from "@/services/utility-service";
import { Button, Form, Input, Modal, Switch, Table, Tag, message } from "antd";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

interface ManufacturerModalProps {
	visible: boolean;
	onClose: () => void;
	onSuccess: () => void;
	currentManufacturers: Manufacturer[];
}

export const ManufacturerModal: FC<ManufacturerModalProps> = ({
	visible,
	onClose,
	onSuccess,
	currentManufacturers,
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [manufacturers, setManufacturers] =
		useState<Manufacturer[]>(currentManufacturers);
	const [manufacturersLoading, setManufacturersLoading] = useState(false);
	const [pageSize, setPageSize] = useState(5);
	const [searchText, setSearchText] = useState("");

	const fetchManufacturers = useCallback(async () => {
		try {
			setManufacturersLoading(true);
			const manufacturersData = await getManufacturers();
			setManufacturers(manufacturersData);
		} catch (error: any) {
			message.error(error.message || "Failed to fetch manufacturers");
		} finally {
			setManufacturersLoading(false);
		}
	}, []);

	useEffect(() => {
		if (visible) {
			fetchManufacturers();
		}
	}, [visible, fetchManufacturers]);

	const handleSubmit = async (values: any) => {
		try {
			setLoading(true);
			await createManufacturer({
				name: values.name.trim(),
				isVerified: values.isVerified || false,
			});

			message.success("Manufacturer created successfully");
			form.resetFields();
			onSuccess();
			fetchManufacturers(); // Refresh the list
		} catch (error: any) {
			message.error(error.message || "Failed to create manufacturer");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setSearchText("");
		onClose();
	};

	// Filter manufacturers based on search text
	const filteredManufacturers = manufacturers.filter((manufacturer) =>
		manufacturer.name.toLowerCase().includes(searchText.toLowerCase()),
	);

	const manufacturerColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Verified",
			dataIndex: "isVerified",
			key: "isVerified",
			render: (isVerified: boolean) => (
				<Tag color={isVerified ? "green" : "default"}>
					{isVerified ? "Verified" : "Not Verified"}
				</Tag>
			),
		},
		{
			title: "Date Added",
			dataIndex: "timeAdded",
			key: "timeAdded",
			render: (value: string) => {
				if (!value) return "-";
				try {
					return new Date(value).toLocaleDateString();
				} catch {
					return value;
				}
			},
		},
	];

	return (
		<Modal
			title="Manage Manufacturers"
			open={visible}
			onCancel={handleCancel}
			footer={[
				<Button key="cancel" onClick={handleCancel}>
					Close
				</Button>,
			]}
			width={1200}
		>
			<div className="space-y-6">
				{/* Current Manufacturers Table */}
				<div>
					<div className="flex justify-between items-center mb-4">
						<h3 className="text-lg font-medium">Current Manufacturers</h3>
						<Input.Search
							placeholder="Search manufacturers..."
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							style={{ width: 300 }}
							allowClear
						/>
					</div>
					<Table
						columns={manufacturerColumns}
						dataSource={filteredManufacturers}
						loading={manufacturersLoading}
						rowKey="id"
						locale={{
							emptyText: searchText
								? `No manufacturers found matching "${searchText}"`
								: "No manufacturers found",
						}}
						pagination={{
							pageSize: pageSize,
							showSizeChanger: true,
							pageSizeOptions: ["5", "10", "20", "50"],
							onShowSizeChange: (current, size) => {
								setPageSize(size);
							},
							showQuickJumper: true,
							showTotal: (total, range) =>
								`${range[0]}-${range[1]} of ${total} manufacturers`,
						}}
						scroll={{ x: 800 }}
					/>
				</div>

				{/* Add New Manufacturer Form */}
				<div className="border-t pt-6">
					<h3 className="text-lg font-medium mb-4">Add New Manufacturer</h3>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						initialValues={{
							isVerified: false,
						}}
					>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Form.Item
								name="name"
								label="Manufacturer Name"
								rules={[
									{ required: true, message: "Manufacturer name is required" },
									{ min: 1, message: "Name must be at least 1 character" },
									{
										max: 255,
										message: "Name must be less than 255 characters",
									},
									{
										validator: (_, value) => {
											if (
												value &&
												manufacturers.some(
													(m) =>
														m.name.toLowerCase() === value.toLowerCase().trim(),
												)
											) {
												return Promise.reject(
													new Error(
														"Manufacturer with this name already exists",
													),
												);
											}
											return Promise.resolve();
										},
									},
								]}
							>
								<Input placeholder="Enter manufacturer name" maxLength={255} />
							</Form.Item>

							<Form.Item
								name="isVerified"
								label="Is Verified"
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
						</div>

						<div className="flex justify-end space-x-2 mt-4">
							<Button onClick={() => form.resetFields()}>Reset</Button>
							<Button type="primary" htmlType="submit" loading={loading}>
								Add Manufacturer
							</Button>
						</div>
					</Form>
				</div>
			</div>
		</Modal>
	);
};
